// Dependencies
import React, { useEffect, useMemo, useState } from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

// Components
import { Avatar } from "@performyard/py-components";

import IconButton from "../../../../components/Button/IconButton";
import InputCheckbox from "../../../../components/Inputs/Checkbox";
import Pagination from "../../../../components/Pagination";
import Spacer from "../../../../components/Spacer";
import TableLite from "../../../../components/TableLite";
import type { Column } from "../../../../components/TableLite/types";
import Text from "../../../../components/Text";
import Tooltip from "../../../../components/Tooltip";
//utils
import { sortRows } from "./DirectoryUtilities";

// API
import { useEmployeeSearch } from "../../../../api/hooks";

import { addUserToSelectionObject, removeUserFromSelectionObject } from "../../../../api/services";

import get from "../../../../utils/GetNested";

//css
import css from "./Directory.module.scss";

interface BaseProps {
	columns: Column[];
	onChangeUsers?: (hasChanges: boolean) => void;
	stagingUserSelectionID?: string;
	selectedEmployees?: string[];
	setSelectedEmployees?: React.Dispatch<React.SetStateAction<string[]>>;
	employeeSearchProps: {
		searchTerm: string;
		userSelectionID?: string;
		limitSelected?: boolean;
		exclusionListId?: string;
	};
	canBulkSelect?: boolean;
	canSingleDelete?: boolean;
	removeUsersFromCohort?: () => Promise<void>;
	isLoading?: boolean;
	nameColumnTitle?: string;
	emptyText?: string;
}

const Directory = (props: BaseProps) => {
	const {
		columns,
		stagingUserSelectionID,
		selectedEmployees,
		setSelectedEmployees,
		canBulkSelect,
		canSingleDelete,
		removeUsersFromCohort,
		isLoading = false,
		nameColumnTitle = "Name",
		emptyText = "No Employees to display",
	} = props;
	const { searchTerm, userSelectionID, limitSelected = false, exclusionListId } = props.employeeSearchProps;

	const [currentPage, setCurrentPage] = useState(1);
	const [activeSortColumnId, setActiveSortColumnId] = useState<string | null>(null);
	const [activeSortDirection, setActiveSortDirection] = useState<"asc" | "desc" | null>(null);

	const { data, isLoading: isEmployeeSearchLoading } = useEmployeeSearch({
		selectionObjectID: userSelectionID,
		searchTerm: searchTerm,
		page: currentPage,
		limitSelected: limitSelected,
		usersToExcludeSelectionId: exclusionListId,
		filters: { activated: ["true"] },
		additionalKeys: [userSelectionID || ""],
	});

	const rows = data?.users ? sortRows(data?.users, activeSortColumnId, activeSortDirection) : [];

	const handleChangePage = (page: number) => {
		setCurrentPage(page);
	};

	const handleAddUser = async (userID: string) => {
		if (setSelectedEmployees && stagingUserSelectionID) {
			setSelectedEmployees((prev) => [...prev, userID]);
			await addUserToSelectionObject(stagingUserSelectionID, [userID]);
		}
	};

	const handleRemoveUser = async (userID: string) => {
		if (setSelectedEmployees && stagingUserSelectionID) {
			setSelectedEmployees((prev) => prev.filter((id) => id !== userID));
			await removeUserFromSelectionObject(stagingUserSelectionID, [userID]);
		}
	};

	/* this initializes rows with already selected users within the selection object
	 it should only be called once when the data is first loaded
	 it is called in the useEffect below
	 it should not be called when the user is deleting single users from the cohort (canSingleDelete)
	 since that assumes no one is in the user selection object when mounting
	*/
	const initializeRowsWithSelectionData = () => {
		const unsortedRows = data?.users;

		if (unsortedRows) {
			unsortedRows.forEach(async (row) => {
				if (get(row, ["user_is_selected"], false) && !selectedEmployees?.includes(row._id)) {
					await handleAddUser(get(row, ["_id"], ""));
				}
			});
		}
	};

	useEffect(() => {
		if (data?.users && !canSingleDelete) {
			initializeRowsWithSelectionData();
		}
	}, [data]);

	// handle columns
	const tableColumns = useMemo(() => {
		if (props.onChangeUsers) {
			if (selectedEmployees) {
				props.onChangeUsers(selectedEmployees.length > 0);
			}
		}

		return [
			{
				id: "name",
				label: nameColumnTitle,
				isSortable: true,
				getter: (row) => {
					const name = get(row, ["name"], "");
					const id = get(row, ["_id"], "");
					let isSelected = false;
					if (selectedEmployees) {
						isSelected = selectedEmployees.includes(row._id);
					}

					const handleToggleUser = async (userID: string) => {
						if (isSelected) {
							handleRemoveUser(userID);
						} else {
							handleAddUser(userID);
						}
					};

					return (
						<div className={`${css.nameColumn} ${canBulkSelect && css.addEmployees}`}>
							{canBulkSelect && (
								<InputCheckbox value={isSelected} onChange={() => handleToggleUser(row._id)} />
							)}
							<Avatar size={24} id={id} name={name || "User"} dangerouslySetClassName={css.avatar} />
							{name.length >= 30 && columns.length > 1 ? (
								<Tooltip message={name}>
									<Text
										size="paragraph-3"
										weight="regular"
										className={`${css.employeeName} ${css.shortened}`}
									>
										{name.slice(0, 27)}...
									</Text>
								</Tooltip>
							) : (
								<Text size="paragraph-3" weight="regular" className={css.employeeName}>
									{name}
								</Text>
							)}
						</div>
					);
				},
			},
			...columns,
			{
				id: "singleDelete",
				label: "",
				width: canSingleDelete && !canBulkSelect ? "50px" : "0px",
				isSortable: false,
				getter: (row) => {
					const isRequesting = selectedEmployees?.includes(row._id);
					return canSingleDelete && !canBulkSelect ? (
						<IconButton
							icon={faTrashCan}
							className={css.deleteButton}
							aria-label="Delete engagement"
							onClick={async () => {
								try {
									/*
										We need to add the user to the staging selection object
										so that the cached selection object id correctly has this employee
										and then update the cohort
										essentially this is updating with an array of 1 user with bulk functionality,
										but we need to do it this way because the cohort change is in selection object
										that is being stored elsewhere
									*/
									await handleAddUser(row._id);
									if (removeUsersFromCohort) {
										await removeUsersFromCohort();
									}
									await handleRemoveUser(row._id);
								} catch (e) {
									await handleRemoveUser(row._id);
								}
							}}
							isRequesting={isRequesting}
							animated
						/>
					) : (
						<div />
					);
				},
			},
		];
	}, [selectedEmployees, stagingUserSelectionID]);

	const onColumnClick = (columnId: string, nextDirection = "desc") => {
		setActiveSortColumnId(columnId);
		setActiveSortDirection(nextDirection as "asc" | "desc");
	};

	return (
		<>
			<TableLite
				theme="vJan23"
				rowKey="_id"
				columns={tableColumns}
				rows={rows}
				onColumnClick={onColumnClick}
				activeSort={activeSortColumnId}
				activeSortDirection={activeSortDirection}
				emptyText={emptyText}
				isLoading={isLoading || isEmployeeSearchLoading}
			/>
			<Spacer height={1} />
			<Pagination currentPage={currentPage} pageCount={data?.pages ?? 1} onClick={handleChangePage} />
		</>
	);
};

export default Directory;
